import * as React from "react";
import useApi from "@/hooks/useApi";
import { Severity } from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";
import { SectionCard } from "@/components/ds";

interface QnAPair {
  id: string;
  question: string;
  answer: string;
}

interface BotStatus {
  vectorEnabled: boolean;
  hasAI: boolean;
  connected: boolean;
  storeUrl: string;
}

export default function WooCommerceBotKnowledge() {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [status, setStatus] = React.useState<BotStatus | null>(null);
  const [pairs, setPairs] = React.useState<QnAPair[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [scanLoading, setScanLoading] = React.useState(false);
  const [scanDone, setScanDone] = React.useState(false);
  const [savedCount, setSavedCount] = React.useState<number | null>(null);
  const [fetchingPolicies, setFetchingPolicies] = React.useState(false);
  const [policyFetchResult, setPolicyFetchResult] = React.useState<
    null | { count: number } | { error: string }
  >(null);

  React.useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [statusResp, qnaResp] = await Promise.all([
          api.admin.get("WooCommerce/Status"),
          api.admin.get("WooCommerce/SuggestedQnA"),
        ]);
        const statusData = await statusResp.json();
        const qnaData = await qnaResp.json();
        setStatus(statusData ?? { vectorEnabled: false, hasAI: false, connected: false, storeUrl: "" });
        if (Array.isArray(qnaData)) {
          setPairs(
            qnaData.map((t: any) => ({
              id: t.id ?? `pair_${Math.random()}`,
              question: t.question ?? "",
              answer: t.answer ?? "",
            }))
          );
        }
      } catch {
        setStatus({ vectorEnabled: false, hasAI: false, connected: false, storeUrl: "" });
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function saveToBot() {
    const filledPairs = pairs.filter((p) => p.answer.trim().length > 0);
    if (filledPairs.length === 0) {
      toastAlert.displayToast(Severity.Warning, "Fill in at least one answer before saving.");
      return;
    }
    setSaving(true);
    try {
      const resp = await api.admin.post(
        "WooCommerce/PushQnA",
        filledPairs.map((p) => ({ question: p.question, answer: p.answer }))
      );
      const data = await resp.json();
      if (data.success) {
        setSavedCount(data.pushed);
        toastAlert.displayToast(Severity.Success, data.message || `${data.pushed} pairs saved to bot.`);
      } else {
        toastAlert.displayToast(Severity.Error, data.message || "Failed to save.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to save to bot.");
    } finally {
      setSaving(false);
    }
  }

  async function scanStore() {
    const storeUrl = status?.storeUrl ?? "";
    if (!storeUrl) {
      toastAlert.displayToast(Severity.Warning, "Connect your WooCommerce store first.");
      return;
    }
    setScanLoading(true);
    try {
      const resp = await api.admin.post("ScraperJobs/Create", {
        name: `WooCommerce store — ${storeUrl}`,
        url: storeUrl.startsWith("http") ? storeUrl : `https://${storeUrl}`,
        maxPages: 100,
        followLinks: true,
      });
      const data = await resp.json();
      if (data.allowed === false) {
        toastAlert.displayToast(Severity.Warning, data.reason || "Scan limit reached.");
      } else if (data.job_id) {
        setScanDone(true);
        toastAlert.displayToast(Severity.Success, `Scanning ${storeUrl} — this takes 2–3 minutes.`);
      } else {
        toastAlert.displayToast(Severity.Error, data.error || "Failed to start scan.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to start scan.");
    } finally {
      setScanLoading(false);
    }
  }

  async function fetchPolicies() {
    const domain = status?.storeUrl ?? "";
    if (!domain) {
      toastAlert.displayToast(Severity.Warning, "Connect your WooCommerce store first.");
      return;
    }
    setFetchingPolicies(true);
    setPolicyFetchResult(null);
    try {
      const resp = await api.admin.get(
        `WooCommerce/FetchPolicies?domain=${encodeURIComponent(domain)}`
      );
      const data = await resp.json();
      if (data.success && Array.isArray(data.pairs)) {
        let filled = 0;
        setPairs((prev) =>
          prev.map((existing) => {
            const match = (data.pairs as QnAPair[]).find((p) => p.id === existing.id);
            if (match && existing.answer.trim() === "" && match.answer.trim() !== "") {
              filled++;
              return { ...existing, answer: match.answer };
            }
            return existing;
          })
        );
        setPolicyFetchResult({ count: filled });
      } else {
        setPolicyFetchResult({ error: data.message || "Could not reach your store policies." });
      }
    } catch {
      setPolicyFetchResult({
        error: "Could not reach your store policies. Make sure your store is connected and try again.",
      });
    } finally {
      setFetchingPolicies(false);
    }
  }

  function addCustomPair() {
    setPairs((prev) => [...prev, { id: `custom_${Date.now()}`, question: "", answer: "" }]);
  }

  function removePair(id: string) {
    setPairs((prev) => prev.filter((p) => p.id !== id));
  }

  function updatePair(id: string, field: "question" | "answer", value: string) {
    setPairs((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const noAI = !status?.hasAI;
  const noVector = !status?.vectorEnabled;
  const filledCount = pairs.filter((p) => p.answer.trim().length > 0).length;

  return (
    <div className="space-y-4">

      {/* Header */}
      <SectionCard
        title="Bot Knowledge"
        description="Teach your bot to answer the questions WooCommerce customers ask most. Fill in your store's answers and the bot will use them in every conversation — instantly, no workflow setup needed."
      >
        {savedCount !== null && (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 mb-2 inline-block">
            {savedCount} pairs active
          </span>
        )}

        {noAI && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
            <strong>AI not enabled on your plan.</strong> Upgrade to enable the bot knowledge feature.
          </div>
        )}
        {!noAI && noVector && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
            <strong>Vector index not configured.</strong> Contact support to enable bot knowledge indexing.
          </div>
        )}
      </SectionCard>

      {/* Auto-fill from store */}
      <SectionCard title="Auto-fill from your store">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-zinc-700 flex items-center gap-2">
            Auto-fill from your store
          </div>
          <p className="text-sm text-zinc-500 mt-0.5">
            Pull your return policy, shipping info, terms, and privacy policy directly from your
            WooCommerce store pages — no copy/paste needed.
          </p>
          {policyFetchResult !== null && "count" in policyFetchResult && (
            <p className="mt-1.5 text-sm text-green-700 font-medium">
              Filled {policyFetchResult.count} of {pairs.length} answers from your store policies.
            </p>
          )}
          {policyFetchResult !== null && "error" in policyFetchResult && (
            <p className="mt-1.5 text-sm text-amber-700 font-medium">
              {policyFetchResult.error}
            </p>
          )}
        </div>
        <button
          type="button"
          disabled={fetchingPolicies || !status?.storeUrl}
          onClick={fetchPolicies}
          className={`flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            fetchingPolicies || !status?.storeUrl
              ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
              : "bg-zinc-800 text-white hover:bg-zinc-900"
          }`}
        >
          {fetchingPolicies ? "Fetching policies..." : "Fill from store \u2192"}
        </button>
      </div>
      </SectionCard>

      {/* Step 1 — Store Scan */}
      <SectionCard title="Step 1 — Scan your store pages">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-zinc-500 mt-1">
              We'll crawl your WooCommerce store and add your pages to the bot's knowledge base.
              This covers products, policies, FAQs, and any content you've published. Takes 2–3
              minutes.{status?.storeUrl && <> Scanning: <strong>{status.storeUrl}</strong>.</>}
            </p>
          </div>
          <button
            type="button"
            disabled={scanLoading || scanDone || !status?.storeUrl}
            onClick={scanStore}
            className={`ml-4 flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              scanDone
                ? "bg-green-100 text-green-700 cursor-default"
                : scanLoading
                ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
                : !status?.storeUrl
                ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {scanDone ? "Scan started" : scanLoading ? "Starting..." : "Scan my store"}
          </button>
        </div>
        {scanDone && (
          <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
            Scan running in the background. Go to <strong>Chatbot Training</strong> to see progress
            and add the pages to your index.
          </div>
        )}
        {!status?.storeUrl && (
          <div className="mt-3 text-xs text-zinc-400">
            Connect your WooCommerce store above to enable scanning.
          </div>
        )}
        <div className="mt-3 p-2 bg-blue-50 border border-blue-100 rounded text-xs text-blue-700">
          <strong>What's indexed vs. live:</strong> This scan captures static pages — product descriptions, policies, FAQs, and blog posts. It does <em>not</em> need to stay up to the minute.{" "}
          <strong>Stock levels, prices, orders, and refunds are always fetched live from WooCommerce</strong> during the conversation via AI skills — never from this scan.{" "}
          To keep your scanned pages refreshed automatically, go to <strong>Chatbot Training → Web</strong> and enable auto-rebuild on a schedule.
        </div>
      </SectionCard>

      {/* Step 2 — QnA Builder */}
      <SectionCard title="Step 2 — Quick Q&A">
        <div className="flex items-start justify-between mb-4">
          <p className="text-sm text-zinc-500">
            Fill in your answers below. The bot will use these to answer customers instantly.
            Leave blank any questions that don't apply to your store.
          </p>
          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
            <span className="text-xs text-zinc-400">
              {filledCount}/{pairs.length} answered
            </span>
            <button
              type="button"
              disabled={saving || filledCount === 0}
              onClick={saveToBot}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                saving || filledCount === 0
                  ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
                  : "bg-green-600 text-white hover:bg-green-700"
              }`}
            >
              {saving ? "Saving..." : "Save to bot"}
            </button>
          </div>
        </div>

        <div className="space-y-3 ml-8">
          {pairs.map((pair) => (
            <div key={pair.id} className="rounded-xl border border-zinc-200 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  {pair.id.startsWith("custom_") ? (
                    <input
                      type="text"
                      value={pair.question}
                      onChange={(e) => updatePair(pair.id, "question", e.target.value)}
                      placeholder="Type your question..."
                      className="w-full text-sm font-medium text-zinc-700 bg-transparent border-b border-zinc-200 pb-1 mb-2 focus:outline-none focus:border-blue-400"
                    />
                  ) : (
                    <div className="text-sm font-medium text-zinc-700 mb-2">{pair.question}</div>
                  )}
                  <textarea
                    rows={3}
                    value={pair.answer}
                    onChange={(e) => updatePair(pair.id, "answer", e.target.value)}
                    placeholder="Type your answer here — be specific about your store's policy..."
                    className="w-full text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded p-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-300"
                  />
                </div>
                {pair.id.startsWith("custom_") && (
                  <button
                    type="button"
                    onClick={() => removePair(pair.id)}
                    aria-label="Remove pair"
                    className="text-zinc-300 hover:text-red-400 mt-1 flex-shrink-0"
                  >
                    <svg
                      viewBox="0 0 16 16"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M4 4l8 8M12 4l-8 8" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addCustomPair}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium py-1"
          >
            <svg
              viewBox="0 0 16 16"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M8 3v10M3 8h10" />
            </svg>
            Add custom question
          </button>
        </div>

        <div className="mt-4 ml-8 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
          <strong>Tip:</strong> The more specific your answers, the better. Instead of "See our
          website", say "We accept returns within 30 days of purchase. Items must be unused and in
          original packaging." Specific answers get much better bot retrieval scores.
        </div>
      </SectionCard>

      {/* Step 3 — Power Features */}
      <SectionCard title="Step 3 — Power features — beyond basic Q&A" description="Your bot can go far beyond answering policy questions. These AI skills let it search your WooCommerce product catalog, look up orders, and retrieve full customer order history — all in the chat window without any agent involvement.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {[
            {
              label: "Product Search",
              tool: "woocommerce_search_products",
              desc: "Searches your WooCommerce product catalog live — name, SKU, price, stock status, and description.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <circle cx="9" cy="9" r="6" /><path d="M15 15l3 3" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Order Lookup",
              tool: "woocommerce_get_order",
              desc: "Gets order details, status, shipping address, and tracking info by order ID.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M3 10h14M10 3l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ),
            },
            {
              label: "Customer Orders",
              tool: "woocommerce_get_customer_orders",
              desc: "Finds all recent orders for a customer email — IDs, statuses, totals, and dates.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <circle cx="10" cy="7" r="4" /><path d="M2 18c0-4 3.6-7 8-7s8 3 8 7" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Cart Link Builder",
              tool: "woocommerce_build_cart_link",
              desc: "Builds a direct add-to-cart URL — customer picks products, bot sends a checkout link with items pre-loaded. Zero friction.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M3 3h2l2.5 10h8l2-6H7.5" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="9" cy="16" r="1" /><circle cx="15" cy="16" r="1" />
                </svg>
              ),
            },
            {
              label: "Coupon Validation",
              tool: "woocommerce_validate_coupon",
              desc: "Validates a discount code in real time — checks if it's active, shows the discount amount and expiry, handles usage limits automatically.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M3 7h14M3 10h14M7 4v12" strokeLinecap="round" />
                  <path d="M3 4h14v12H3z" rx="1.5" />
                </svg>
              ),
            },
            {
              label: "Live Stock Check",
              tool: "woocommerce_check_stock",
              desc: "Checks real-time inventory — uses urgency language when stock is low ('Only 3 left!') and handles out-of-stock and backorder states.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M10 3v3M10 14v3M3 10h3M14 10h3" strokeLinecap="round" />
                  <circle cx="10" cy="10" r="4" />
                </svg>
              ),
            },
            {
              label: "Customer Lookup",
              tool: "woocommerce_get_customer",
              desc: "Looks up a customer by email — profile, order count, lifetime spend, phone, and company.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <circle cx="10" cy="7" r="4" /><path d="M2 18c0-4 3.6-7 8-7s8 3 8 7" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Cancel Order",
              tool: "woocommerce_cancel_order",
              desc: "Cancels a WooCommerce order by setting its status to cancelled. Always confirm with the customer first.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <circle cx="10" cy="10" r="7" /><path d="M7 7l6 6M13 7l-6 6" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Get Refunds",
              tool: "woocommerce_get_refunds",
              desc: "Lists all refunds for an order — amounts, reasons, dates, and who issued them.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M4 10h12M4 10l4-4M4 10l4 4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ),
            },
            {
              label: "Create Refund",
              tool: "woocommerce_create_refund",
              desc: "Processes a partial or full refund on an order. Specify the amount and reason.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M10 3v14M5 10h10" strokeLinecap="round" />
                  <circle cx="10" cy="10" r="7" />
                </svg>
              ),
            },
            {
              label: "Order Notes",
              tool: "woocommerce_get_order_notes",
              desc: "Retrieves internal and customer-facing notes on an order — communication history at a glance.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <rect x="3" y="3" width="14" height="14" rx="2" /><path d="M7 7h6M7 10h4" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Add Order Note",
              tool: "woocommerce_create_order_note",
              desc: "Adds a note to an order — internal for staff or visible to the customer.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M4 4h12v12H4zM8 8h4M8 11h2" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Update Order Status",
              tool: "woocommerce_update_order_status",
              desc: "Changes an order's status — processing, on-hold, completed, cancelled, or refunded.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M3 10l4 4 10-10" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ),
            },
            {
              label: "Search Orders",
              tool: "woocommerce_search_orders",
              desc: "Searches orders by customer name, email, or order number. Returns matching orders with status and totals.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <circle cx="9" cy="9" r="6" /><path d="M15 15l3 3" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Product by SKU",
              tool: "woocommerce_get_product_by_sku",
              desc: "Looks up a product by exact SKU — precise identification for inventory and support queries.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <rect x="3" y="5" width="14" height="10" rx="1" /><path d="M6 8v4M8 8v4M10 8v4M12 8v4M14 8v4" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Product Variations",
              tool: "woocommerce_get_product_variations",
              desc: "Lists all variations (sizes, colors, etc.) for a variable product with prices and stock levels.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <rect x="3" y="3" width="6" height="6" rx="1" /><rect x="11" y="3" width="6" height="6" rx="1" /><rect x="3" y="11" width="6" height="6" rx="1" /><rect x="11" y="11" width="6" height="6" rx="1" />
                </svg>
              ),
            },
            {
              label: "Product Reviews",
              tool: "woocommerce_get_product_reviews",
              desc: "Retrieves customer reviews — ratings, text, verified purchase badge. Great for social proof.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M10 2l2.5 5 5.5.8-4 3.9.9 5.5L10 14.7 5.1 17.2l.9-5.5-4-3.9L7.5 7z" strokeLinejoin="round" />
                </svg>
              ),
            },
            {
              label: "Categories",
              tool: "woocommerce_get_categories",
              desc: "Lists product categories with hierarchy and product counts — helps customers browse the catalog.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M3 5h4l2 2h8v10H3z" strokeLinejoin="round" />
                </svg>
              ),
            },
            {
              label: "Shipping Methods",
              tool: "woocommerce_get_shipping_methods",
              desc: "Lists shipping zones and available methods with costs — answer 'how much is shipping?' instantly.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M2 12h12V5H2zM14 12h3l2 3v2h-5z" strokeLinejoin="round" /><circle cx="6" cy="16" r="1.5" /><circle cx="16" cy="16" r="1.5" />
                </svg>
              ),
            },
            {
              label: "Store Settings",
              tool: "woocommerce_get_store_settings",
              desc: "Retrieves store name, currency, timezone, WooCommerce version, and tax/shipping configuration.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <circle cx="10" cy="10" r="3" /><path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.2 4.2l1.4 1.4M14.4 14.4l1.4 1.4M4.2 15.8l1.4-1.4M14.4 5.6l1.4-1.4" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Tax Rates",
              tool: "woocommerce_get_tax_rates",
              desc: "Lists configured tax rates by country and state — answer tax questions with real data.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M4 4h12v12H4z" /><path d="M4 4l12 12M7 4v5M4 8h5M11 16v-5M16 12h-5" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Payment Gateways",
              tool: "woocommerce_get_payment_gateways",
              desc: "Lists available payment methods — tell customers exactly how they can pay at checkout.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <rect x="2" y="5" width="16" height="10" rx="2" /><path d="M2 9h16" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Sales Report",
              tool: "woocommerce_get_sales_report",
              desc: "Summarizes sales for a time period — totals, order count, average sale, tax, and shipping revenue.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M3 17V7l4-4 4 6 4-2 2 4v6z" strokeLinejoin="round" />
                </svg>
              ),
            },
            {
              label: "Top Sellers",
              tool: "woocommerce_get_top_sellers",
              desc: "Shows best-selling products for a time period — great for recommendations and trending items.",
              tier: "Professional",
              comingSoon: false,
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M5 17V9M10 17V3M15 17V7" strokeLinecap="round" />
                </svg>
              ),
            },
          ].map((f, i) => (
            <div
              key={i}
              className={`rounded-xl border p-4 flex gap-3 ${f.comingSoon ? "border-dashed border-zinc-200 opacity-70" : "border-zinc-200"}`}
            >
              <div className="mt-0.5 flex-shrink-0 text-blue-500">
                {f.icon}
              </div>
              <div>
                <div className="text-sm font-semibold text-zinc-700 flex items-center gap-2">
                  {f.label}
                  <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                    {f.tier}
                  </span>
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">{f.desc}</div>
                <div className="text-xs text-zinc-400 mt-1 font-mono">{f.tool}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 bg-zinc-50 border border-zinc-200 rounded text-xs text-zinc-600">
          <strong>To enable:</strong> Go to <strong>Bots &rarr; AI Training &rarr; Skills</strong> and enable the
          WooCommerce Commerce skills (24 tools). These require a Professional plan and a connected WooCommerce store.
        </div>
      </SectionCard>
    </div>
  );
}
